import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faCheck,
  faExternalLinkAlt,
  faChartLine,
  faLink,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { generateReferralLink } from "@/features/embajador/embajadorThunks";

/**
 * GenerateReferralLink
 *
 * Componente para que embajadores generen y gestionen sus links de referido por evento
 *
 * @param {string} eventoUid - UID del evento para generar el link
 * @param {string} eventoNombre - Nombre del evento (opcional, para mostrar)
 */
const GenerateReferralLink = ({ eventoUid, eventoNombre }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [referralLink, setReferralLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateLink = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dispatch(
        generateReferralLink({ eventoUid })
      ).unwrap();
      console.log("Generated referral link:", result);

      setReferralLink(result?.referralLink);
    } catch (err) {
      console.error("Error generating referral link:", err);
      setError(err || "Error al generar el link de referido");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!referralLink?.url) return;

    navigator.clipboard
      .writeText(referralLink.url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Error copying to clipboard:", err);
        alert("Error al copiar el link");
      });
  };

  const handleOpenLink = () => {
    if (!referralLink?.url) return;
    window.open(referralLink.url, "_blank");
  };
console.log("referralLink: ", referralLink);
  return (
    <Card className="w-full bg-aw-bgbluedark py-0 gap-0 border border-aw-bluegray">
      <CardHeader
        className={`cursor-pointer hover:bg-aw-green/25 transition-colors p-2 ${isOpen ? "rounded-tl-xl rounded-tr-xl" : "rounded-xl"} space-y-0`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between pr-4">
          <div className="flex flex-col justify-center gap-2">
            <CardTitle className="text-white text-sm">
              <FontAwesomeIcon
                icon={faLink}
                className="w-5 h-5 mr-2 text-aw-green"
              />
              Herramientas de Embajador
            </CardTitle>
            <CardDescription className="text-white text-xs">
              Genera tu link único para compartir y ganar comisiones
            </CardDescription>
          </div>
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className="w-4 h-4 text-white"
          />
        </div>
      </CardHeader>

      {isOpen && (
        <CardContent className="space-y-4 bg-aw-bluegray/15">
          {!referralLink ? (
            // Mostrar botón para generar link
            <div className="text-center py-6">
              <FontAwesomeIcon
                icon={faLink}
                size="2x"
                className="mb-2 text-aw-green"
              />
              <p className="text-white mb-4">
                {eventoNombre
                  ? `Ver mi link de referido para ${eventoNombre}`
                  : "Ver mi link de referido para este evento"}
              </p>
              <Button
                onClick={handleGenerateLink}
                disabled={loading}
                className="w-full sm:w-auto bg-aw-green border border-aw-green hover:bg-aw-bluedark text-aw-bluedark hover:text-aw-green focus:ring focus:ring-aw-green"
              >
                <FontAwesomeIcon icon={faLink} className="mr-2" />
                {loading ? "Cargando..." : "Ver Link de Referido"}
              </Button>

              {error && <p className="text-red-600 mt-4 text-sm">{error}</p>}
            </div>
          ) : (
            // Mostrar link generado
            <>
              <div className="border-b pb-4 py-4">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2 text-aw-green">
                  <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
                  Tu Link de Referido
                </h3>
                <p className="text-sm text-white">
                  {eventoNombre
                    ? <>Comparte este link del Evento: <b className="text-aw-green underline">{eventoNombre}</b></>
                    : "Comparte este link para ganar comisiones"}
                </p>
              </div>

              {/* URL del link */}
              <div className="flex gap-2">
                <div className="flex-1 p-3 bg-gray-100 rounded-md text-sm font-mono overflow-x-auto">
                  {referralLink?.url && (
                    <FontAwesomeIcon
                      icon={faLink}
                      className="inline-block mr-2"
                    />
                  )}
                  {referralLink?.url || "Cargando..."}
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-2">
                <Button
                  onClick={handleCopyLink}
                  className="flex-1 bg-aw-green border border-aw-green hover:bg-aw-bluedark text-aw-bluedark hover:text-aw-green focus:ring focus:"
                  disabled={!referralLink}
                >
                  {copied ? (
                    <>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="w-4 h-4 mr-2"
                      />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCopy} className="w-4 h-4 mr-2" />
                      Copiar Link
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleOpenLink}
                  variant="outline"
                  disabled={!referralLink}
                >
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    className="w-4 h-4"
                  />
                </Button>
              </div>

              {/* Código de referido */}
              {referralLink?.code && (
                <div className="py-2 border-t">
                  <p className="text-xs text-white mb-1">Tu código:</p>
                  <p className="font-mono font-bold text-lg text-aw-green">
                    {referralLink.code}
                  </p>
                </div>
              )}

              {/* Estadísticas */}
              {referralLink?.stats && (
                <div className="grid grid-cols-3 gap-4 py-4 border-t">
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-orange-500 mb-1"
                    />
                    <p className="text-2xl font-bold text-orange-400">
                      {referralLink.stats.totalClicks || 0}
                    </p>
                    <p className="text-xs text-white font-semibold tracking-wider">Clicks</p>
                  </div>
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-aw-green/80 mb-1"
                    />
                    <p className="text-2xl font-bold text-aw-green">
                      {referralLink.stats.totalConversions || 0}
                    </p>
                    <p className="text-xs text-white font-semibold tracking-wider">Ventas</p>
                  </div>
                  <div className="text-center">
                    <FontAwesomeIcon
                      icon={faChartLine}
                      className="text-blue-500 mb-1"
                    />
                    <p className="text-2xl font-bold text-blue-400">
                      $
                      {referralLink.stats?.totalComisionAmbassador?.toLocaleString() ||
                        0}
                    </p>
                    <p className="text-xs text-white font-semibold tracking-wider">Comisiones</p>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default GenerateReferralLink;
